import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = `${window.location.origin}/api`;

const SiteSettingsContext = createContext(null);

export const useSiteSettings = () => {
  const context = useContext(SiteSettingsContext);
  if (!context) {
    throw new Error("useSiteSettings must be used within SiteSettingsProvider");
  }
  return context;
};

export const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
      applyTheme(response.data);
    } catch (error) {
      console.error("Failed to fetch settings:", error);
      // Use defaults
      const defaults = {
        site_name: "ODR Academy",
        site_subtitle: "Estetica Oncologica",
        primary_color: "#B87A8A",
        secondary_color: "#F3F0FA",
        accent_color: "#C5A065",
        background_color: "#FFFCF9",
        text_color: "#2D2A32",
      };
      setSettings(defaults);
    } finally {
      setLoading(false);
    }
  };

  const applyTheme = (themeSettings) => {
    if (!themeSettings) return;
    
    const root = document.documentElement;
    
    // Convert hex to HSL for CSS variables
    const hexToHSL = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      if (!result) return null;
      
      let r = parseInt(result[1], 16) / 255;
      let g = parseInt(result[2], 16) / 255;
      let b = parseInt(result[3], 16) / 255;
      
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h, s, l = (max + min) / 2;
      
      if (max === min) {
        h = s = 0;
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
          case g: h = ((b - r) / d + 2) / 6; break;
          case b: h = ((r - g) / d + 4) / 6; break;
          default: h = 0;
        }
      }
      
      return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
    };

    if (themeSettings.primary_color) {
      const hsl = hexToHSL(themeSettings.primary_color);
      if (hsl) root.style.setProperty('--primary', hsl);
    }
    
    if (themeSettings.secondary_color) {
      const hsl = hexToHSL(themeSettings.secondary_color);
      if (hsl) root.style.setProperty('--secondary', hsl);
    }
    
    if (themeSettings.accent_color) {
      const hsl = hexToHSL(themeSettings.accent_color);
      if (hsl) root.style.setProperty('--accent', hsl);
    }
    
    if (themeSettings.background_color) {
      const hsl = hexToHSL(themeSettings.background_color);
      if (hsl) root.style.setProperty('--background', hsl);
    }
    
    if (themeSettings.text_color) {
      const hsl = hexToHSL(themeSettings.text_color);
      if (hsl) root.style.setProperty('--foreground', hsl);
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      const response = await axios.put(`${API}/admin/settings`, newSettings);
      setSettings(response.data);
      applyTheme(response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const refreshSettings = () => {
    fetchSettings();
  };

  return (
    <SiteSettingsContext.Provider value={{ settings, loading, updateSettings, refreshSettings }}>
      {children}
    </SiteSettingsContext.Provider>
  );
};
